import { useEffect, useRef, useState } from "react";

import {
  WidgetCustomizationAction,
  WidgetCustomizationActionCreateAttributes,
  WidgetCustomizationFreshPageMeta,
} from "~/api/Api";

import { Icon } from "../../assets/icons/Icon";
import { formatNumber } from "../../helpers/helpers";
import MiddleTruncateTextPx from "../dashboard/PageTable/MiddleTruncateUrl";
import Button from "../mantine/button/Button";
import Spinner from "../ui/spinner/Spinner";
import CurrentItem from "./ContentItem";
import styles from "./styles.module.scss";

interface ContentAttrs {
  name?: string;
  content?: string;
  type?: string;
  [key: string]: string | undefined;
}

export interface ContentItem {
  title?: string;
  total?: number;
  approved?: number;
  path_and_query?: string;
  id?: number;
  active?: boolean;
  edited?: boolean;
  global?: boolean;
  kind?: string;
  action?: string;
  content_type?: string;
  path?: string;
  content?: string | null;
  tag?: string;
  attrs?: ContentAttrs | null;
  created_at?: string;
  updated_at?: string;
  suggestion?: string | null;
  contentful?: boolean;
  lang?: string | null;
  provider?: string;
  source?: "auto" | "manual" | "regenerate" | null;
  outdated?: boolean;
  fresh_page_meta?: WidgetCustomizationFreshPageMeta;
}

export interface ApproveData {
  kind: string;
  variant: string;
  page_id?: number;
  site_id?: number;
  ids?: number[];
}

export interface ContentItemsProps {
  data: ContentItem[];
  currenPageUrl?: string;
  approved?: number;
  total?: number;
  isPending?: boolean;
  approve: (
    data: WidgetCustomizationActionCreateAttributes,
  ) => Promise<WidgetCustomizationAction> | void;
  pageId?: number;
  group?: "enhancement" | "ai" | "schema" | "ai_instructions";
  pendingStatuses?: WidgetCustomizationAction[];
  isPendingAprove?: boolean;
  isPageSuggestionsApproved?: boolean;
  setIsPageSuggestionsApproved?: (value: boolean) => void;
  approvePermission?: boolean;
  isFetchingStats?: boolean;
  isPageApproveClicked?: boolean;
  setIsPageApproveClicked?: (value: boolean) => void;
  isLoading?: boolean;
  isPagesLoadingNoData?: boolean;
  setCurrentItem?: (item: ContentItem[]) => void;
  itemAuthorizations?: string[][];
}

const ContentItems = ({
  data,
  currenPageUrl = "",
  approved = 0,
  total = 0,
  approve,
  pageId,
  group = "ai",
  pendingStatuses = [],
  isPendingAprove = false,
  isPageSuggestionsApproved = false,
  setIsPageSuggestionsApproved = () => {},
  approvePermission = false,
  isFetchingStats,
  isLoading,
  isPagesLoadingNoData,
  setCurrentItem,
  itemAuthorizations = [],
}: ContentItemsProps): JSX.Element => {
  // ai.md manual actions need the AI-engine + prerendering add-ons, surfaced as
  // the per-row member `update` ability. Other groups are unaffected.
  const canUpdateItem = (index: number): boolean =>
    group === "ai_instructions"
      ? (itemAuthorizations[index]?.includes("update") ?? false)
      : true;
  // Page-level "Approve All" — enabled when the page's ai.md items are updatable.
  const canUpdate =
    group === "ai_instructions"
      ? itemAuthorizations.some((auth) => auth?.includes("update"))
      : true;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);

  useEffect((): (() => void) => {
    const updateWidth = (): void => {
      if (wrapperRef.current) {
        setWrapperWidth(wrapperRef.current.offsetWidth);
      }
    };
    updateWidth();

    window.addEventListener("resize", updateWidth);

    return (): void => window.removeEventListener("resize", updateWidth);
  }, []);

  const allApproved: boolean = data?.every(
    (item: ContentItem): boolean => !!item.active,
  );

  const handleApprovePageSggestions = (): void => {
    setIsPageSuggestionsApproved(true);

    if (approve && pageId) {
      approve({
        kind: allApproved ? "unapprove" : "approve",
        variant: group,
        page_id: pageId,
      });
    }
  };

  const hasPagePendingOperation: boolean =
    !!pageId &&
    pendingStatuses.some(
      (item: WidgetCustomizationAction): boolean =>
        item.status === "pending" && item.page_id === pageId,
    );

  if (isPagesLoadingNoData) {
    return <Spinner />;
  }

  return (
    // @ts-expect-error TS2551 non-existing className
    <div className={styles.pageItemWrapper}>
      <div className={styles.pageItemHeader}>
        <div className={styles.pageItemUrl} ref={wrapperRef}>
          <MiddleTruncateTextPx
            text={currenPageUrl || ""}
            maxWidth={wrapperWidth}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          />
        </div>
        <Button
          variant="outlinedIconSmall"
          onClick={handleApprovePageSggestions}
          disabled={
            !canUpdate ||
            !pageId ||
            !approve ||
            !approvePermission ||
            hasPagePendingOperation ||
            isPendingAprove ||
            pendingStatuses?.length > 0 ||
            isFetchingStats
          }
        >
          {hasPagePendingOperation ||
          (isFetchingStats && isPageSuggestionsApproved) ||
          (isPendingAprove && isPageSuggestionsApproved) ? (
            <Button variant="nohoverWithPadding">
              <Spinner size="1rem" />
            </Button>
          ) : (
            <Icon
              iconName={allApproved ? "GreenCheckmarkIcon" : "CloseIconCircled"}
              width="1rem"
              height="1rem"
            />
          )}
          {allApproved ? "Approved" : "Approve All"}
        </Button>
      </div>
      <div className={styles.pageItemApproved}>
        {allApproved ? (
          <span>{formatNumber(approved)} approved</span>
        ) : (
          <span>
            {formatNumber(approved)} of {formatNumber(total)} approved
          </span>
        )}
      </div>
      <div className={styles.pageItemTitle}>
        {data?.map((item: ContentItem, index: number) => (
          // @ts-expect-error TS2339 non-existing className
          <div className={styles.pageItem} key={index}>
            <CurrentItem
              setCurrentItem={setCurrentItem}
              item={item}
              isLoading={isLoading}
              approve={approve}
              group={group}
              pendingStatuses={pendingStatuses}
              isPendingAprove={isPendingAprove}
              approvePermission={approvePermission}
              pageId={pageId || 0}
              wrapperWidth={wrapperWidth}
              canDestroy={
                itemAuthorizations[index]?.includes("destroy") ?? false
              }
              canUpdate={canUpdateItem(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContentItems;
